import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, HeartPulse, Car, Coffee, Briefcase, Route as RouteIcon } from "lucide-react";
import { PageLoading, PageHeader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const hm = (s) => `${Math.floor((s || 0) / 3600)}h ${String(Math.floor(((s || 0) % 3600) / 60)).padStart(2, "0")}m`;
const time = (iso) => iso ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }) : "—";
const ICON = { VISIT: HeartPulse, TRAVEL: Car, BREAK: Coffee, OTHER: Briefcase };
const TONE = { VISIT: "bg-cyan/10 text-cyan-dark", TRAVEL: "bg-amber-50 text-amber-700", BREAK: "bg-slate-100 text-slate-500", OTHER: "bg-slate-100 text-slate-600" };

const Stat = ({ label, value, testid }) => (<div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div><div className="mt-1 font-heading text-2xl font-extrabold text-navy">{value}</div></div>);

export default function CareDay() {
  const [date, setDate] = useState(today());
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { setD(null); api.get(`/worker/care/my-day?date=${date}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [date]);
  if (d === false) return <div className="py-20 text-center text-slate-400">Nicht verfügbar.</div>;
  if (!d) return <PageLoading />;
  const s = d.summary;

  return (
    <div data-testid="worker-care-day">
      <PageHeader icon={Clock} title="Meine Arbeitszeit" action={
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="wday-date" />
      } />
      <button onClick={() => nav("/app/worker/care")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Meine Tour</button>

      <div className="mb-2 flex items-center gap-2 text-sm"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500" data-testid="wday-status">{d.daily_statuses[s.status]}</span>{s.open_shift && <span className="text-amber-600">Dienst läuft / unvollständig</span>}</div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Netto" value={hm(s.net_work_seconds)} testid="wday-net" />
        <Stat label="Besuchszeit" value={hm(s.visit_seconds)} testid="wday-visit" />
        <Stat label="Fahr-/Wegezeit" value={hm(s.travel_seconds)} testid="wday-travel" />
        <Stat label="Sonstige" value={hm(s.other_work_seconds)} testid="wday-other" />
        <Stat label="Pause" value={hm(s.break_seconds)} />
        <Stat label="Kilometer" value={`${(s.distance_meters / 1000).toFixed(1)} km`} testid="wday-km" />
        <Stat label="Besuche" value={s.visit_count} testid="wday-count" />
        <Stat label="Brutto" value={hm(s.gross_shift_seconds)} />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="wday-timeline">
        <div className="mb-3 flex items-center gap-2"><RouteIcon className="h-4.5 w-4.5 text-cyan-dark" /><h3 className="font-heading font-bold text-navy">Tagesablauf</h3></div>
        <div className="relative space-y-3 pl-5">
          <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" />
          {d.timeline.map((t, i) => { const I = ICON[t.type] || Briefcase; return (
            <div key={i} className="relative" data-testid={`wday-seg-${i}`}>
              <span className="absolute -left-[13px] top-1.5 h-2.5 w-2.5 rounded-full bg-cyan" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><span className={`flex h-6 w-6 items-center justify-center rounded-lg ${TONE[t.type]}`}><I className="h-3.5 w-3.5" /></span>
                  <span className="text-sm font-semibold text-navy">{t.type === "VISIT" ? (t.patient_number || "Patient") : t.label}</span></div>
                <span className="text-xs text-slate-400">{time(t.start)}–{time(t.end)} · {hm(t.seconds)}{t.distance_meters ? ` · ${(t.distance_meters / 1000).toFixed(1)} km` : t.type === "TRAVEL" && t.distance_status === "NOT_CALCULATED" ? " · km n/v" : ""}</span>
              </div>
            </div>
          ); })}
          {d.timeline.length === 0 && <div className="text-sm text-slate-400">Keine Aktivität an diesem Tag.</div>}
        </div>
      </div>
    </div>
  );
}
